import type { ResponseType } from '@/interfaces/database/base';

const OPERATING_ERROR_CODE = 103;

const FRIENDLY_ERROR_MESSAGES: Record<string, string> = {
  'Only owner of canvas authorized for this operation.':
    '您不是该智能体的创建者，无法执行此操作。请使用自己创建的智能体，或联系创建者处理。',
};

const REASON_CODE_MESSAGES: Record<string, string> = {
  SHARED_AGENT_READ_ONLY: '这是团队共享智能体，您可以查看和运行，但不能修改。',
  AGENT_ACCESS_DENIED: '智能体不存在或您无权访问。',
  DEPENDENCY_ACCESS_DENIED: '该智能体依赖的知识资源尚未授权，请联系管理员。',
  NO_ALLOW_RULE: '您暂时没有该资源的访问权限。',
  EXPLICIT_DENY: '当前权限策略禁止执行此操作。',
  CLEARANCE_TOO_LOW: '您的安全访问级别不足。',
  RESOURCE_NOT_FOUND: '请求的资源不存在或您无权查看。',
  RESOURCE_INACTIVE: '该资源已停用。',
  POLICY_CHANGED: '权限已发生变化，请刷新页面后重试。',
  UNAUTHENTICATED: '登录状态已失效，请重新登录。',
  ACCOUNT_DISABLED: '当前账号已停用，请联系管理员。',
  INVALID_API_CREDENTIAL: 'API 凭据无效。',
  API_CREDENTIAL_REVOKED: 'API 凭据已被撤销。',
  PUBLISH_CREDENTIAL_MISSING: '分享链接缺少发布凭据。',
  PUBLISH_CREDENTIAL_INVALID: '分享链接无效，请向发布人获取新链接。',
  PUBLISH_LINK_REVOKED: '分享链接已撤销或已重新生成。',
  PUBLISH_LINK_EXPIRED: '分享链接已过期。',
  PUBLISH_SESSION_EXPIRED: '分享会话已过期，请重新打开分享链接。',
  LEGACY_PUBLISH_LINK_RETIRED: '旧版分享链接已停用，请获取重新发布后的链接。',
  MODEL_UNAVAILABLE: '当前模型暂不可用，请联系管理员检查模型配置。',
  AGENT_RUN_FAILED: '智能体运行失败，请稍后重试。',
  INTERNAL_ERROR: '系统处理失败，请稍后重试。',
};

const USER_SESSION_FAILURE_REASONS = new Set([
  'UNAUTHENTICATED',
  'ACCOUNT_DISABLED',
  'AUTH_SESSION_EXPIRED',
  'AUTH_CREDENTIAL_REVOKED',
]);

const PUBLIC_AUTH_PATHS = new Set([
  '/login',
  '/login-next',
  '/chat/share',
  '/next-chat/share',
]);

const hasChineseText = (value: string) => /[\u3400-\u9fff]/.test(value);

type ApiErrorData = Partial<
  Pick<ResponseType, 'code' | 'message' | 'reason_code' | 'request_id'>
>;

export const shouldClearUserSession = (
  data?: ApiErrorData,
  httpStatus?: number,
) => {
  if (PUBLIC_AUTH_PATHS.has(window.location.pathname)) return false;
  if (httpStatus !== 401 && data?.code !== 401) return false;

  // A reason code identifies the credential domain. API credentials and
  // published links must never clear an interactive user's browser session.
  // Legacy endpoints can return a bare HTTP/body 401, which does mean the
  // current protected-page login has expired.
  if (data?.reason_code) {
    return USER_SESSION_FAILURE_REASONS.has(data.reason_code);
  }
  return true;
};

export const getFriendlyApiErrorMessage = (
  data?: ApiErrorData,
  fallback = '操作未完成，请稍后重试。',
) => {
  const backendMessage = String(data?.message || '').trim();
  if (backendMessage && hasChineseText(backendMessage)) {
    return backendMessage;
  }

  const reasonMessage = data?.reason_code
    ? REASON_CODE_MESSAGES[data.reason_code]
    : undefined;
  if (reasonMessage) {
    return reasonMessage;
  }

  const legacyMessage = FRIENDLY_ERROR_MESSAGES[backendMessage];
  if (legacyMessage) {
    return legacyMessage;
  }

  return fallback;
};

export const getApiErrorNotification = (data: ApiErrorData, hint: string) => {
  const description = getFriendlyApiErrorMessage(data);

  return {
    key: `api-error-${data.reason_code || data.code || 'unknown'}`,
    message: data.code === OPERATING_ERROR_CODE ? '无法完成操作' : hint,
    description,
  };
};
